import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, WifiOff, Share, PlusSquare, MonitorSmartphone, Bell, X } from "lucide-react";

// Détection de l'appareil pour afficher les bonnes instructions d'installation
function detectDevice() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  if (isIOS) return "ios";
  if (isAndroid) return "android";
  return "desktop";
}

const INSTRUCTIONS = {
  ios: {
    title: "Sur votre iPhone / iPad",
    steps: [
      "Touchez le bouton Partager (carré avec une flèche) en bas de Safari",
      "Faites défiler et touchez « Sur l'écran d'accueil »",
      "Touchez « Ajouter » : l'icône du Bistroquet apparaît sur votre écran",
    ],
  },
  android: {
    title: "Sur votre téléphone / tablette Android",
    steps: [
      "Touchez le bouton Installer ci-dessous, ou le menu ⋮ de Chrome",
      "Choisissez « Installer l'application » / « Ajouter à l'écran d'accueil »",
      "L'icône du Bistroquet apparaît sur votre écran d'accueil",
    ],
  },
  desktop: {
    title: "Sur votre ordinateur",
    steps: [
      "Cliquez sur l'icône d'installation à droite de la barre d'adresse (ou le bouton Installer ci-dessous)",
      "Confirmez « Installer » : l'application s'ouvre dans sa propre fenêtre",
      "Retrouvez-la dans votre menu Démarrer / Launchpad comme un vrai logiciel",
    ],
  },
};

export default function PwaInstall() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [pushAsked, setPushAsked] = useState(false);
  const [pushDenied, setPushDenied] = useState(false);
  const device = detectDevice();

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    // Proposer l'installation à chaque visiteur, une fois par session.
    // La fenêtre REVIENT à chaque visite tant que les notifications ne sont pas activées
    // (elles sont indispensables : confirmation de commande, enlèvement prêt, arrivages).
    const installed = window.matchMedia("(display-mode: standalone)").matches || navigator.standalone;
    const seen = sessionStorage.getItem("pwa_prompt_seen");
    const pushOk = "Notification" in window && Notification.permission === "granted";
    if ((!installed || !pushOk) && !seen) {
      const t = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(t);
    }
  }, []);

  const install = async () => {
    // 1. Activer les notifications (étape indispensable de l'installation)
    await askPush();
    // 2. Lancer l'installation si le navigateur la propose (Android / ordinateur)
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    }
    setVisible(false);
    sessionStorage.setItem("pwa_prompt_seen", "1");
  };

  const askPush = async () => {
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (Notification.permission !== "default") return;
    setPushAsked(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm === "granted") {
        const { api } = await import("@/lib/api");
        const reg = await navigator.serviceWorker.ready;
        const res = await api.get("/push/vapid-key");
        const pad = "=".repeat((4 - (res.data.key.length % 4)) % 4);
        const b64 = (res.data.key + pad).replace(/-/g, "+").replace(/_/g, "/");
        const key = Uint8Array.from([...window.atob(b64)].map((c) => c.charCodeAt(0)));
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
        await api.post("/push/subscribe", sub.toJSON());
      } else if (perm === "denied") {
        setPushDenied(true);
      }
    } catch {
      // L'utilisateur pourra l'activer plus tard depuis la cloche
    } finally {
      setPushAsked(false);
    }
  };

  const close = () => {
    setVisible(false);
    // « Plus tard » ferme pour la session entière ; la fenêtre revient à la prochaine visite
    // tant que les notifications ne sont pas activées
    sessionStorage.setItem("pwa_prompt_seen", "1");
  };

  const steps = INSTRUCTIONS[device];
  const notifPerm = "Notification" in window ? Notification.permission : "unsupported";

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="pwa-install-modal"
          >
            <div className="absolute inset-0 bg-[#1C1917]/60 backdrop-blur-sm" onClick={close} />
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full sm:max-w-md bg-[#FAF6F0] rounded-t-3xl sm:rounded-3xl p-6 sm:p-7 shadow-2xl"
              style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
              data-lenis-prevent
            >
              <button onClick={close} className="absolute top-4 right-4 p-2 rounded-full text-[#78716C] hover:bg-[#6B1226]/10 transition-colors" data-testid="pwa-install-close" aria-label="Fermer">
                <X size={18} />
              </button>
              <div className="flex items-center gap-4">
                <img src="/icons/icon-192.png" alt="Les Vins du Bistroquet" className="w-16 h-16 rounded-2xl shadow-lg no-frame" data-testid="pwa-install-icon" />
                <div>
                  <p className="font-display text-xl font-semibold text-[#1C1917] leading-tight">L'application du Bistroquet</p>
                  <p className="text-xs text-[#57534E] mt-1 flex items-center gap-1.5">
                    <MonitorSmartphone size={13} className="text-[#6B1226]" /> Téléphone, tablette, ordinateur
                  </p>
                </div>
              </div>
              <p className="mt-4 text-sm text-[#44403C] leading-relaxed">
                Installez l'application avec son icône : elle s'ouvre en plein écran, plus rapide, et vous recevez nos arrivages et offres en direct.
              </p>
              <div className="mt-4 bg-[#F5EEDC] rounded-2xl border border-[#C5A059]/50 p-4 flex items-start gap-3" data-testid="pwa-push-notice">
                <Bell size={17} className="text-[#8C6D23] shrink-0 mt-0.5" />
                <p className="text-xs text-[#44403C] leading-relaxed">
                  <span className="font-bold text-[#6B1226]">Notifications indispensables :</span> acceptez-les pour être prévenu dès que votre commande est prête à retirer, et recevoir nos arrivages et offres en direct.
                </p>
              </div>
              {(pushDenied || notifPerm === "denied") && (
                <p className="mt-3 text-xs font-semibold text-[#6B1226] bg-[#6B1226]/5 border border-[#6B1226]/20 rounded-xl px-4 py-3" data-testid="pwa-push-denied">
                  Les notifications sont bloquées par le navigateur. Activez-les dans les réglages du site (cadenas à gauche de l'adresse) pour recevoir l'alerte « commande prête ».
                </p>
              )}
              <div className="mt-4 bg-white rounded-2xl border border-[#6B1226]/10 p-4">
                <p className="font-eyebrow text-[0.6rem] tracking-[0.2em] uppercase font-bold text-[#C5A059] flex items-center gap-1.5">
                  {device === "ios" ? <Share size={12} /> : device === "android" ? <PlusSquare size={12} /> : <Download size={12} />}
                  {steps.title}
                </p>
                <ol className="mt-2.5 space-y-2 text-xs text-[#44403C] leading-relaxed list-none">
                  {steps.steps.map((s, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#6B1226] text-[#FAF6F0] flex items-center justify-center text-[0.6rem] font-bold shrink-0">{i + 1}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
              {notifPerm === "default" ? (
                <button
                  onClick={install}
                  disabled={pushAsked}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#6B1226] text-[#FAF6F0] py-3.5 text-sm font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-60"
                  data-testid="pwa-install-button"
                >
                  <Bell size={16} /> {pushAsked ? "Activation des notifications…" : "Installer et activer les notifications"}
                </button>
              ) : deferred ? (
                <button
                  onClick={install}
                  disabled={pushAsked}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#6B1226] text-[#FAF6F0] py-3.5 text-sm font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-60"
                  data-testid="pwa-install-button"
                >
                  <Download size={16} /> Installer l'application
                </button>
              ) : null}
              <button onClick={close} className="mt-3 w-full text-center text-xs text-[#78716C] hover:text-[#6B1226] transition-colors" data-testid="pwa-install-later">
                Plus tard
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {offline && (
        <div
          className="fixed inset-x-0 z-40 bg-[#1C1917] text-[#FAF6F0] text-center text-xs py-2 flex items-center justify-center gap-2"
          style={{ top: "calc(3.5rem + env(safe-area-inset-top, 0px))" }}
          data-testid="pwa-offline-indicator"
        >
          <WifiOff size={13} /> Vous êtes hors ligne — la cave reste consultable
        </div>
      )}
    </>
  );
}
